import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Compass, Home, Sparkles, Users, Mail, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const LINKS = [
  { to: "/", label: "Home", desc: "Begin again at the heart of the ecosystem", icon: Home, id: "home" },
  { to: "/offerings", label: "Offerings", desc: "Wellness sessions, programs and packages", icon: Sparkles, id: "offerings" },
  { to: "/discover", label: "Discover", desc: "Explore everything happening across the community", icon: Compass, id: "discover" },
  { to: "/enhancers", label: "Enhancers", desc: "Meet the practitioners guiding the way", icon: Users, id: "enhancers" },
  { to: "/contact", label: "Contact", desc: "Tell us what you were looking for", icon: Mail, id: "contact" },
];

const useNoIndex = () => {
  useEffect(() => {
    const el = document.createElement("meta");
    el.name = "robots";
    el.content = "noindex, follow";
    el.dataset.notfound = "1";
    document.head.appendChild(el);
    const prerender = document.createElement("meta");
    prerender.name = "prerender-status-code";
    prerender.content = "404";
    document.head.appendChild(prerender);
    return () => { el.remove(); prerender.remove(); };
  }, []);
};

export default function NotFound() {
  const { pathname } = useLocation();
  useNoIndex();
  return (
    <section className="px-6 py-24 md:py-32" data-testid="not-found-page">
      <Seo title="Page not found" description="The page you were looking for has moved or no longer exists. Continue your journey with Enhancing Your World." />
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-[#144F75]/70" data-testid="not-found-code">404 · Path not found</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A242B] mt-4 max-w-3xl">This path has wandered off — but your journey hasn't.</h1>
        <p className="text-[#4A5D6B] mt-5 max-w-2xl text-sm md:text-base">
          We couldn't find <span className="font-mono text-[#144F75] break-all" data-testid="not-found-path">{pathname}</span>. Enhancing Your World Enhances Our World — choose a pathway below to keep going.
        </p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LINKS.map(({ to, label, desc, icon: Icon, id }) => (
            <Link key={id} to={to} data-testid={`not-found-link-${id}`} className="group rounded-2xl border border-[#144F75]/10 bg-white p-5 shadow-sm transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-md">
              <Icon size={22} className="text-[#144F75]" />
              <div className="mt-3 flex items-center gap-2 font-serif text-lg text-[#1A242B]">
                {label}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </div>
              <p className="text-sm text-[#4A5D6B] mt-1">{desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
